import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, TrophyIcon, UserGroupIcon, ChartBarIcon } from '@heroicons/react/24/solid';

const Projects = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15
            }
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-stone-900/70 z-10"></div>
                    <img
                        src="https://images.unsplash.com/photo-1595831551608-2510b642e12e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1766&q=80"
                        alt="Projects Hero"
                        className="w-full h-full object-cover opacity-60"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-7xl font-thin mb-6"
                    >
                        Our <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-600">Impact</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg md:text-xl text-gray-300 font-light max-w-2xl mx-auto"
                    >
                        Showcasing real-world implementation and success stories.
                    </motion.p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

                {/* Ongoing Projects */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={staggerContainer}
                    className="mb-24"
                >
                    <div className="text-center mb-12">
                        <span className="text-orange-600 font-bold tracking-widest uppercase text-sm">Innovation in Action</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Ongoing Projects</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { title: "Smart Farming Pilot", img: require('../../assets/images/smart_farming_pilot.png'), desc: "Implementing IoT sensors and automated irrigation in test plots to monitor soil moisture levels dynamically." },
                            { title: "Digital Agriculture Africa", img: require('../../assets/images/digital_agriculture_africa.png'), desc: "Empowering strongholder farmers with mobile-based crop advisory and market linkage services." },
                            { title: "Precision Irrigation", img: require('../../assets/images/precision_irrigation_project.png'), desc: "Large scale deployment of drip irrigation systems integrated with weather stations." }
                        ].map((project, idx) => (
                            <motion.div
                                key={idx}
                                variants={fadeInUp}
                                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
                            >
                                <div className="h-56 overflow-hidden relative">
                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10"></div>
                                    <img src={project.img} alt={project.title} className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110" />
                                </div>
                                <div className="p-8">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                        <span className="text-xs font-bold text-green-600 uppercase tracking-wider">Active</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">{project.title}</h3>
                                    <p className="text-gray-600 font-light leading-relaxed">{project.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Completed Projects */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={staggerContainer}
                    className="mb-24"
                >
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Completed Success Stories</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {/* Project 1 */}
                        <motion.div variants={fadeInUp} className="bg-white rounded-3xl p-2 shadow-lg flex flex-col md:flex-row gap-6 border border-gray-100 hover:border-gray-200 transition-colors">
                            <div className="w-full md:w-2/5 h-64 md:h-auto rounded-2xl overflow-hidden relative">
                                <img src={require('../../assets/images/services/expert_consultancy.png')} alt="Project" className="w-full h-full object-cover" />
                            </div>
                            <div className="w-full md:w-3/5 p-4 md:py-8 md:pr-8 flex flex-col justify-center">
                                <span className="inline-block bg-blue-50 text-blue-600 text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider w-fit mb-4">Success Story</span>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">AI Based Crop Advisory 2024</h3>
                                <p className="text-gray-600 mb-6 font-light">Successfully guided over 500 farmers to increase yield by 20% through data-driven insights.</p>
                                <div className="flex items-center gap-2 text-primary font-bold">
                                    <TrophyIcon className="h-5 w-5" /> Award Winning
                                </div>
                            </div>
                        </motion.div>

                        {/* Project 2 */}
                        <motion.div variants={fadeInUp} className="bg-white rounded-3xl p-2 shadow-lg flex flex-col md:flex-row gap-6 border border-gray-100 hover:border-gray-200 transition-colors">
                            <div className="w-full md:w-2/5 h-64 md:h-auto rounded-2xl overflow-hidden relative">
                                <img src={require('../../assets/images/services/hydroponics.png')} alt="Project" className="w-full h-full object-cover" />
                            </div>
                            <div className="w-full md:w-3/5 p-4 md:py-8 md:pr-8 flex flex-col justify-center">
                                <span className="inline-block bg-green-50 text-green-600 text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider w-fit mb-4">Community Impact</span>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">Sustainable Farming Initiative</h3>
                                <p className="text-gray-600 mb-6 font-light">Promoted organic farming practices in 5 villages, resulting in healthier soil and premium produce pricing.</p>
                                <div className="flex items-center gap-2 text-green-600 font-bold">
                                    <UserGroupIcon className="h-5 w-5" /> 5 Villages
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Statistics / Impact */}
                <motion.section
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    className="bg-gray-900 rounded-[2.5rem] p-12 md:p-20 text-white text-center relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-black/50"></div>

                    <div className="relative z-10">
                        <ChartBarIcon className="h-16 w-16 text-primary mx-auto mb-6" />
                        <h2 className="text-3xl md:text-5xl font-bold mb-16">Measuring Our Impact</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 divide-y md:divide-y-0 md:divide-x divide-white/10">
                            {[
                                { val: "5000+", label: "Farmers Benefited" },
                                { val: "25%", label: "Yield Increase" },
                                { val: "40%", label: "Water Saved" }
                            ].map((stat, idx) => (
                                <div key={idx} className="p-4">
                                    <div className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-400 mb-2">{stat.val}</div>
                                    <div className="text-lg text-primary font-medium tracking-wide">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.section>
            </div>
        </div>
    );
};

export default Projects;
